import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, LockIcon, ZapIcon, ShieldCheckIcon, CreditCardIcon } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { goToCheckout, type Plan } from '../lib/checkout';
const planDetails: Record<Plan, {
  cta: string;
  bullets: string[];
}> = {
  pro: {
    cta: 'QUIERO EL PACK PROFESIONAL →',
    bullets: ['12 Tomos completos (todos los niveles)', '6 Bonus estratégicos', '+300 páginas A4', '+200 ejercicios graficados', 'Acceso inmediato', 'Fichas listas para imprimir', '100% adaptado a Argentina']
  },
  elite: {
    cta: 'SÍ, QUIERO EL MEGAPACK ÉLITE →',
    bullets: ['TODO el Pack Profesional incluido', 'Ebook Élite 1: Stretching Seguro 360', 'Ebook Élite 2: Stretching para la Mujer y Maternidad', '40 páginas adicionales de conocimiento especializado', 'Protocolos para embarazo y postparto', 'Manejo de hernias, prótesis y condiciones sistémicas', 'Acceso al nicho de mayor rentabilidad del mercado']
  }
};
export function PricingSection() {
  const [selected, setSelected] = useState<Plan>('elite');
  const handleCheckout = () => {
    goToCheckout(selected);
  };
  return <section id="precios" className="py-16 px-4 bg-primary" data-id="element-173">
      <div className="max-w-2xl mx-auto" data-id="element-174">
        {/* Countdown */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-175">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-176">
            ⏰ PRECIO DE LANZAMIENTO — OFERTA POR TIEMPO LIMITADO
          </h2>
          <CountdownTimer data-id="element-177" />
          <p className="text-xl text-highlight font-bold mt-6" data-id="element-178">
            🔴 El precio sube cuando termina el contador
          </p>
        </motion.div>

        {/* Selectable panel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-3xl p-6 md:p-10 shadow-2xl" data-id="element-179">
          <div className="text-center mb-8" data-id="element-180">
            <h3 className="text-3xl md:text-4xl font-black text-gray-900 mb-3" data-id="element-181">
              Elegí tu Pack:
            </h3>
            <p className="text-gray-500 text-lg" data-id="element-182">
              Ambas opciones incluyen los 6 Bonus estratégicos
            </p>
          </div>

          <div className="space-y-5" data-id="element-183">
            {/* Pack Profesional */}
            <button type="button" onClick={() => setSelected('pro')} aria-pressed={selected === 'pro'} className={`w-full text-left rounded-2xl border-2 p-5 transition-colors flex items-center gap-4 ${selected === 'pro' ? 'border-accent bg-accent/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-184">
              <Radio active={selected === 'pro'} data-id="element-185" />
              <div className="flex-1 min-w-0" data-id="element-186">
                <h4 className="text-lg md:text-xl font-bold text-gray-900 leading-tight" data-id="element-187">
                  📘 PACK PROFESIONAL
                </h4>
                <p className="text-sm text-gray-500 mt-1" data-id="element-188">12 Tomos + 6 Bonus</p>
              </div>
              <div className="text-right flex-shrink-0" data-id="element-189">
                <div className="text-sm text-gray-400 line-through" data-id="element-190">
                  $29.990
                </div>
                <div className="text-2xl md:text-3xl font-black text-gray-900" data-id="element-191">
                  $18.990
                </div>
              </div>
            </button>

            {/* Megapack Élite */}
            <button type="button" onClick={() => setSelected('elite')} aria-pressed={selected === 'elite'} className={`w-full text-left rounded-2xl border-2 p-5 transition-colors flex items-center gap-4 ${selected === 'elite' ? 'border-accent bg-accent/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-192">
              <Radio active={selected === 'elite'} data-id="element-193" />
              <div className="flex-1 min-w-0" data-id="element-194">
                <h4 className="text-lg md:text-xl font-bold text-gray-900 leading-tight mb-2" data-id="element-195">
                  🏆 MEGAPACK ÉLITE
                </h4>
                <div className="flex flex-wrap gap-2 mb-2" data-id="element-196">
                  <span className="inline-flex items-center gap-1 bg-highlight text-primary px-3 py-0.5 rounded-full text-xs font-bold" data-id="element-197">
                    ⭐ MÁS ELEGIDO
                  </span>
                  <span className="inline-block bg-accent/15 text-accent px-3 py-0.5 rounded-full text-xs font-bold" data-id="element-198">
                    AHORRÁS $10.000
                  </span>
                </div>
                <p className="text-sm text-gray-500" data-id="element-199">
                  12 Tomos + 6 Bonus + 2 EBOOKS ÉLITE
                </p>
              </div>
              <div className="text-right flex-shrink-0" data-id="element-200">
                <div className="text-sm text-gray-400 line-through" data-id="element-201">
                  $49.990
                </div>
                <div className="text-2xl md:text-3xl font-black text-accent" data-id="element-202">
                  $27.990
                </div>
              </div>
            </button>
          </div>

          {/* Selected plan details */}
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-6 mb-8" data-id="element-203">
            {planDetails[selected].bullets.map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-204">
                <CheckIcon className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" data-id="element-205" />
                <span className="text-sm text-gray-600" data-id="element-206">{item}</span>
              </li>)}
          </ul>

          {/* CTA */}
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={handleCheckout} className="w-full bg-highlight text-primary py-5 rounded-2xl font-bold text-lg md:text-xl hover:bg-highlight-dark transition-colors shadow-lg" data-id="element-207">
            {planDetails[selected].cta}
          </motion.button>

          <p className="text-center text-sm text-gray-400 italic mt-5" data-id="element-208">
            La opción más elegida por instructores de stretching argentinos
          </p>

          {/* Trust row */}
          <div className="grid grid-cols-2 gap-y-4 gap-x-6 mt-8 pt-6 border-t border-gray-100" data-id="element-209">
            <TrustItem icon={LockIcon} label="Pago seguro" data-id="element-210" />
            <TrustItem icon={ZapIcon} label="Acceso en 5 min" iconClass="text-highlight" data-id="element-211" />
            <TrustItem icon={ShieldCheckIcon} label="Garantía 7 días" iconClass="text-sky-600" data-id="element-212" />
            <TrustItem icon={CreditCardIcon} label="Tarjeta · Transferencia" data-id="element-213" />
          </div>
        </motion.div>
      </div>
    </section>;
}
function Radio({
  active
}: {
  active: boolean;
}) {
  return <span className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors ${active ? 'border-accent' : 'border-gray-300'}`} data-id="element-214">
      {active && <span className="w-3.5 h-3.5 rounded-full bg-accent" data-id="element-215" />}
    </span>;
}
function TrustItem({
  icon: Icon,
  label,
  iconClass = 'text-gray-700'
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  iconClass?: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-216">
      <Icon className={`w-5 h-5 flex-shrink-0 ${iconClass}`} data-id="element-217" />
      <span className="text-sm text-gray-600" data-id="element-218">{label}</span>
    </div>;
}